import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import TagVisual from "./TagVisual";

/**
 * Interaction Moment — the highlight of the page.
 * Press "Call" → user bubble appears → delay → tag glows & responds.
 */
const InteractionMoment = () => {
  const [state, setState] = useState("idle"); // idle | calling | responding
  const [autoKey, setAutoKey] = useState(0);

  const trigger = () => {
    if (state !== "idle") return;
    setState("calling");
    setTimeout(() => setState("responding"), 1100);
    setTimeout(() => {
      setState("idle");
      setAutoKey((k) => k + 1);
    }, 4200);
  };

  return (
    <section
      id="interaction"
      data-testid="interaction-section"
      className="relative py-28 md:py-36 bg-[#0A0A0A] text-white overflow-hidden"
    >
      {/* Ambient */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-blue-600/25 blur-[140px] rounded-full" />
      </div>
      <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:72px_72px] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="max-w-2xl">
          <p className="text-xs tracking-[0.22em] uppercase text-blue-300/80 font-medium">The Moment</p>
          <h2 className="mt-3 font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1]">
            Your things <span className="italic font-light text-zinc-400">should answer you.</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
            Not on a map. Not in an app. Right here — in the room, out loud.
          </p>
        </div>

        <div className="mt-16 md:mt-24 grid lg:grid-cols-2 gap-12 items-center">
          {/* Dialogue side */}
          <div className="order-2 lg:order-1 space-y-4">
            <motion.div
              key={`call-${autoKey}-${state}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: state === "idle" ? 0.35 : 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="flex"
              data-testid="moment-user-bubble"
            >
              <div className="max-w-[82%] glass-dark rounded-3xl rounded-bl-sm px-5 py-4">
                <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">You</div>
                <div className="mt-1 font-display text-xl md:text-2xl tracking-tight">
                  "Keys… where are you?"
                </div>
              </div>
            </motion.div>

            <motion.div
              key={`reply-${autoKey}-${state}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: state === "responding" ? 1 : 0.1, y: state === "responding" ? 0 : 8 }}
              transition={{ duration: 0.5, delay: state === "responding" ? 0.05 : 0 }}
              className="flex justify-end"
              data-testid="moment-tag-bubble"
            >
              <div className="relative max-w-[82%] bg-white text-black rounded-3xl rounded-br-sm px-5 py-4 shadow-[0_20px_60px_rgba(59,130,246,0.25)]">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                  <span className="text-[10px] tracking-[0.22em] uppercase text-blue-600">VoloTag</span>
                </div>
                <div className="mt-1 font-display text-xl md:text-2xl tracking-tight">
                  "Here — by the sofa."
                </div>
                {/* live wave */}
                <div className="mt-3 flex items-end gap-1 h-5">
                  {Array.from({ length: 24 }).map((_, b) => (
                    <motion.span
                      key={b}
                      className="w-[3px] rounded-full bg-blue-500/90"
                      animate={state === "responding" ? { height: [4, 4 + ((b * 11) % 16), 4] } : { height: 4 }}
                      transition={{ duration: 0.9 + (b % 3) * 0.12, repeat: Infinity, ease: "easeInOut", delay: b * 0.03 }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>

            <div className="pt-6">
              <Button
                onClick={trigger}
                disabled={state !== "idle"}
                data-testid="moment-call-button"
                className="group rounded-full bg-white text-black hover:bg-zinc-200 h-12 px-7 cta-shine disabled:opacity-70"
              >
                <Mic className="w-4 h-4 mr-2" />
                {state === "idle" && "Call a VoloTag"}
                {state === "calling" && "Calling…"}
                {state === "responding" && "Here it is"}
              </Button>
              <p className="mt-3 text-xs text-zinc-500">Tap to see the response in action.</p>
            </div>
          </div>

          {/* Tag + animated response side */}
          <div className="order-1 lg:order-2 relative flex justify-center lg:justify-end">
            <div className="relative">
              {/* Response glow (only when responding) */}
              <motion.div
                initial={false}
                animate={{ opacity: state === "responding" ? 1 : 0.35, scale: state === "responding" ? 1.05 : 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="absolute inset-0 -m-10 rounded-full bg-blue-500/30 blur-3xl"
              />
              <TagVisual size={440} listening />

              {/* Extra strong ring burst on response */}
              {state === "responding" && (
                <>
                  {[0, 0.25, 0.5].map((d, i) => (
                    <motion.span
                      key={i}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full border-2 border-blue-400/60"
                      initial={{ scale: 0.8, opacity: 0.9 }}
                      animate={{ scale: 3.2, opacity: 0 }}
                      transition={{ duration: 1.6, delay: d, ease: "easeOut" }}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractionMoment;
